import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    Star, Calendar, BarChart2, Globe, Users, ArrowRight,
    CheckCircle, Building, Zap, Shield
} from 'lucide-react';
import SEO from '../components/SEO';

const BENEFITS = [
    { icon: Calendar, title: 'Event Listings', desc: 'Submit and manage your cultural events with full admin support and approval flow.' },
    { icon: BarChart2, title: 'Audience Reach', desc: 'Tap into Singapore\'s Indian Heritage & Arts community — thousands of engaged visitors.' },
    { icon: Globe, title: 'Online Presence', desc: 'Your events featured on the ATAM Connect platform with dedicated event detail pages.' },
    { icon: Users, title: 'Community Network', desc: 'Connect with other cultural organisations, artists, and event promoters.' },
    { icon: Zap, title: 'Fast Approvals', desc: 'Dedicated partner dashboard — submit, edit, and track event status in real time.' },
    { icon: Shield, title: 'Trusted Platform', desc: 'Backed by ATAM — Singapore\'s trusted Indian heritage and arts network.' },
];

const HOW_IT_WORKS = [
    { step: '01', title: 'Apply to Join', desc: 'Contact the ATAM team to get your partner account set up.' },
    { step: '02', title: 'Access Dashboard', desc: 'Log in to your dedicated partner portal to manage everything in one place.' },
    { step: '03', title: 'Submit Events', desc: 'Fill in event details and submit for admin approval — usually within 24 hours.' },
    { step: '04', title: 'Go Live', desc: 'Your event is published and promoted to the ATAM Connect audience.' },
];

export default function PartnerPortalLandingPage() {
    const { user, isPartner, isAdmin } = useAuth();
    const navigate = useNavigate();

    // If already a partner/admin, go straight to dashboard
    const handleCTA = () => {
        if (isPartner || isAdmin) navigate('/partner');
        else navigate('/login');
    };

    return (
        <main className="main" style={{ paddingTop: 0 }}>
            <SEO
                title="Partner Portal | ATAM Connect"
                description="Become a cultural partner with ATAM Connect. Submit events, reach Singapore's Indian Heritage & Arts audience, and grow your organisation."
            />

            {/* ── Title Header ────────────────────────────────────────── */}
            <div style={{
                paddingTop: '40px',
                textAlign: 'center',
                background: '#1a0a2e',
                borderBottom: '1px solid rgba(255,255,255,0.05)'
            }}>
                <h1 style={{
                    fontSize: 'clamp(40px, 8vw, 72px)',
                    fontWeight: 900,
                    margin: 0,
                    padding: '0 24px 20px',
                    letterSpacing: '-0.02em',
                    background: 'linear-gradient(135deg, #de41b0, #fecd6d)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textTransform: 'uppercase'
                }}>
                    Partner-Portal
                </h1>
            </div>

            {/* ── Hero ───────────────────────────────────────────────── */}
            <section style={{
                background: 'linear-gradient(135deg, #1a0a2e 0%, #2d1166 40%, #5b1a8a 80%, #de41b0 100%)',
                padding: '100px 24px 80px',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
            }}>
                {/* Decorative circles */}
                <div style={{ position: 'absolute', top: -80, right: -80, width: 320, height: 320, borderRadius: '50%', background: 'rgba(254,205,109,0.07)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: -60, left: -60, width: 240, height: 240, borderRadius: '50%', background: 'rgba(222,65,176,0.12)', pointerEvents: 'none' }} />

                <div style={{ position: 'relative', maxWidth: 700, margin: '0 auto' }}>
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        background: 'rgba(254,205,109,0.15)', border: '1px solid rgba(254,205,109,0.3)',
                        color: '#fecd6d', borderRadius: 20, padding: '4px 14px',
                        fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.08em',
                        textTransform: 'uppercase', marginBottom: 24,
                    }}>
                        <Star size={12} /> Partner Programme
                    </span>

                    <h1 style={{
                        fontSize: 'clamp(32px, 6vw, 56px)', fontWeight: 900,
                        color: '#fff', lineHeight: 1.15, marginBottom: 20,
                    }}>
                        Bring Your Events to<br />
                        <span style={{
                            background: 'linear-gradient(90deg, #fecd6d, #ff8a65)',
                            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                        }}>ATAM Connect</span>
                    </h1>

                    <p style={{
                        fontSize: '1.1rem', color: 'rgba(255,255,255,0.75)',
                        maxWidth: 540, margin: '0 auto 36px', lineHeight: 1.7,
                    }}>
                        Join Singapore's premier Indian Heritage & Arts platform as a partner.
                        List your cultural events, grow your audience, and connect with the community.
                    </p>

                    <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button onClick={handleCTA} style={{
                            background: 'linear-gradient(135deg, #de41b0, #fecd6d)',
                            color: '#1a0a2e', border: 'none', borderRadius: 50,
                            padding: '14px 32px', fontSize: '1rem', fontWeight: 800,
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                            boxShadow: '0 8px 24px rgba(222,65,176,0.4)',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                        }}
                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(222,65,176,0.5)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(222,65,176,0.4)'; }}
                        >
                            {(isPartner || isAdmin) ? 'Go to Dashboard' : 'Partner Login'} <ArrowRight size={18} />
                        </button>
                        <Link to="/contact" style={{
                            background: 'rgba(255,255,255,0.1)', color: '#fff',
                            border: '1px solid rgba(255,255,255,0.25)', borderRadius: 50,
                            padding: '14px 32px', fontSize: '1rem', fontWeight: 700,
                            textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8,
                            backdropFilter: 'blur(8px)',
                        }}>
                            Contact Us
                        </Link>
                    </div>
                </div>
            </section>

            {/* ── Benefits ───────────────────────────────────────────── */}
            <section style={{ padding: '80px 24px', maxWidth: 1100, margin: '0 auto' }}>
                <div style={{ textAlign: 'center', marginBottom: 56 }}>
                    <h2 style={{ fontSize: 'clamp(24px, 4vw, 38px)', fontWeight: 800, marginBottom: 12 }}>
                        Why Partner With Us?
                    </h2>
                    <p style={{ color: 'var(--text-secondary)', maxWidth: 500, margin: '0 auto' }}>
                        Everything you need to showcase your events to the right audience.
                    </p>
                </div>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: 24,
                }}>
                    {BENEFITS.map(({ icon: Icon, title, desc }) => (
                        <div key={title} className="card" style={{ padding: '28px 24px' }}>
                            <div style={{
                                width: 48, height: 48, borderRadius: 14,
                                background: 'linear-gradient(135deg, rgba(222,65,176,0.12), rgba(254,205,109,0.12))',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                marginBottom: 16,
                            }}>
                                <Icon size={22} style={{ color: 'var(--primary)' }} />
                            </div>
                            <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 8 }}>{title}</h3>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', lineHeight: 1.6 }}>{desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── How It Works ───────────────────────────────────────── */}
            <section style={{ background: 'var(--bg-secondary)', padding: '80px 24px' }}>
                <div style={{ maxWidth: 900, margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: 56 }}>
                        <h2 style={{ fontSize: 'clamp(22px, 4vw, 36px)', fontWeight: 800, marginBottom: 12 }}>
                            How It Works
                        </h2>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 32 }}>
                        {HOW_IT_WORKS.map(({ step, title, desc }) => (
                            <div key={step} style={{ textAlign: 'center' }}>
                                <div style={{
                                    fontSize: '2.5rem', fontWeight: 900,
                                    background: 'linear-gradient(135deg, #de41b0, #fecd6d)',
                                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                    marginBottom: 12, lineHeight: 1,
                                }}>{step}</div>
                                <h3 style={{ fontWeight: 700, marginBottom: 8 }}>{title}</h3>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', lineHeight: 1.6 }}>{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Final CTA ──────────────────────────────────────────── */}
            <section style={{
                padding: '80px 24px', textAlign: 'center',
                background: 'linear-gradient(135deg, rgba(222,65,176,0.06), rgba(254,205,109,0.06))',
                borderTop: '1px solid var(--border)',
            }}>
                <Building size={40} style={{ color: 'var(--primary)', marginBottom: 20 }} />
                <h2 style={{ fontSize: 'clamp(22px, 4vw, 36px)', fontWeight: 800, marginBottom: 12 }}>
                    Ready to List Your Events?
                </h2>
                <p style={{ color: 'var(--text-secondary)', maxWidth: 460, margin: '0 auto 32px', lineHeight: 1.7 }}>
                    Already a partner? Log in to your dashboard. New to ATAM Connect? Get in touch with us to join.
                </p>
                <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button onClick={handleCTA} className="btn btn-primary" style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '13px 30px', fontSize: '0.95rem',
                    }}>
                        {(isPartner || isAdmin) ? 'Go to Dashboard' : 'Partner Login'} <ArrowRight size={18} />
                    </button>
                    <Link to="/contact" className="btn btn-secondary" style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '13px 30px', fontSize: '0.95rem',
                    }}>
                        Contact ATAM Team
                    </Link>
                </div>

                {/* Checklist */}
                <div style={{
                    display: 'flex', gap: 24, justifyContent: 'center', flexWrap: 'wrap',
                    marginTop: 40, color: 'var(--text-secondary)', fontSize: '0.875rem',
                }}>
                    {['Free to join', 'Fast approvals', 'Full event control', 'Dedicated support'].map(item => (
                        <span key={item} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <CheckCircle size={14} style={{ color: 'var(--primary)' }} /> {item}
                        </span>
                    ))}
                </div>
            </section>
        </main>
    );
}
